import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@api/client';
import { useAuth } from '@shared/hooks/useAuth';

/**
 * Счётчики раздела «Сообщения» для бейджей меню.
 * Обе величины — число АБОНЕНТОВ (не сообщений), как в v4 BodyHtml.php:1102-1140.
 */
export interface MessagesCount {
  /** Абоненты с неархивными непустыми сообщениями. */
  unread: number;
  /** Из них те, чьи сообщения новее отметки date_last_msg текущего оператора. */
  new: number;
}

/** Строка списка непрочитанных — эталон backend/internal/message/domain/message.go:MessageResponse. */
export interface UnreadMessage {
  id: number;
  customer_id: number;
  customer_name: string;
  text: string;
  created_at: string;
  is_archive: boolean;
}

/**
 * Полная форма ответа `/messages/unread`. Объявлена ЗДЕСЬ и только здесь: этот файл уже
 * несёт общий queryKey, и хук шапки (features/header/hooks/useMessages.ts) переиспользует
 * и тип, и fetch-функцию, а не заводит свою копию. До этой правки два независимых
 * объявления одного эндпоинта разошлись по составу полей (здесь `messages` не было вовсе) —
 * при общем ключе кеша сужение любого из двух тихо ломает другого читателя без ошибки типов.
 */
export interface UnreadMessagesResponse {
  messages: UnreadMessage[];
  unread_count: number;
  new_count: number;
}

/**
 * Тот же ключ, что и у хука шапки (features/header/hooks/useMessages.ts).
 * Эндпоинт один, и общий ключ означает ОДНУ запись кеша: шапка и меню физически
 * не могут показать разные числа про один и тот же факт. С разными ключами это
 * были бы два запроса в разные моменты и два расходящихся значения.
 */
export const messagesUnreadQueryKey = ['messages', 'unread'] as const;

export async function fetchUnreadMessages(): Promise<UnreadMessagesResponse> {
  const { data } = await apiClient.get<UnreadMessagesResponse>('/messages/unread', {
    params: { limit: 5 },
  });
  return data;
}

export function useMessagesCount() {
  const { hasPermission } = useAuth();

  return useQuery({
    queryKey: messagesUnreadQueryKey,
    queryFn: fetchUnreadMessages,
    // Раздел закрыт правом customers.messages (бэкенд отвечает 403). Без гейта хук
    // раз в 30 с стучался бы в запретный эндпоинт. Гейт обязан стоять в ОБОИХ хуках,
    // читающих этот ключ: ключ кеша общий, и негейченный хук наполнил бы кеш, из
    // которого гейченный отрисовал бы бейдж.
    enabled: hasPermission('customers.messages'),
    refetchInterval: 30_000,
    staleTime: 20_000,
    // Совпадает с хуком шапки: у общего ключа опции берутся от последнего
    // наблюдателя, и разное значение retry сделало бы поведение недетерминированным
    // (0 или 3 повтора с backoff каждые 30 с на каждом экране).
    retry: false,
    select: (data): MessagesCount => ({
      unread: data?.unread_count ?? 0,
      new: data?.new_count ?? 0,
    }),
  });
}
